import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

/** The column a table is ordered by, and the direction. */
export interface TableSortState<K extends string = string> {
  key: K;
  dir: SortDir;
}

/**
 * The value a row is ordered by in one column: text (ISO dates included) or a number. `null`,
 * `undefined`, `''` and `NaN` are empty and sort last in both directions.
 */
export type SortValue = string | number | boolean | null | undefined;

/** Value getters of the sortable columns of a table, by column key. */
export type SortColumns<T, K extends string> = Readonly<Record<K, (row: T) => SortValue>>;

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
const ISO_DATE = /^\d{4}-\d{2}/;

const isEmpty = (value: SortValue): value is null | undefined | '' =>
  value === null ||
  value === undefined ||
  value === '' ||
  (typeof value === 'number' && Number.isNaN(value));

function compare(a: SortValue, b: SortValue): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return collator.compare(String(a), String(b));
}

/**
 * Rows ordered by one column. Empty values sort last in both directions; text compares
 * case-insensitively with embedded numbers in numeric order; ties keep their order (stable).
 */
export function sortRows<T>(rows: readonly T[], value: (row: T) => SortValue, dir: SortDir): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, key: value(row) }))
    .sort((a, b) => {
      const aEmpty = isEmpty(a.key);
      const bEmpty = isEmpty(b.key);
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? a.index - b.index : aEmpty ? 1 : -1;
      return sign * compare(a.key, b.key) || a.index - b.index;
    })
    .map(({ row }) => row);
}

/** Text sorts A–Z first; numbers, flags and dates largest or newest first. */
export function firstDirection<T>(rows: readonly T[], value: (row: T) => SortValue): SortDir {
  for (const row of rows) {
    const v = value(row);
    if (isEmpty(v)) continue;
    return typeof v === 'string' && !ISO_DATE.test(v) ? 'asc' : 'desc';
  }
  return 'asc';
}

/** Props of a sortable column header (`SortHeader`), from `useTableSort().header(key)`. */
export interface SortHeaderState<K extends string = string> {
  column: K;
  sort: TableSortState<K> | null;
  onSort: (column: K) => void;
}

export interface SortOrder<T, K extends string> {
  sort: TableSortState<K> | null;
  toggle: (key: K) => void;
  /** Props for the `SortHeader` of a column. */
  header: (key: K) => SortHeaderState<K>;
  /** Rows in the selected order (as given until a header is selected), e.g. one group of a table. */
  order: (rows: readonly T[]) => T[];
}

/**
 * The sort state of a table whose rows are all loaded, for tables that sort each group of rows on
 * its own (`order(group)`); `rows` (all of them) decides the first direction of a column.
 */
export function useSortOrder<T, K extends string>(
  rows: readonly T[],
  columns: SortColumns<T, K>,
  initial: TableSortState<NoInfer<K>> | null = null,
): SortOrder<T, K> {
  const [sort, setSort] = useState(initial);
  const toggle = (key: K) => {
    setSort((current) =>
      current?.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: firstDirection(rows, columns[key]) },
    );
  };
  return {
    sort,
    toggle,
    header: (key) => ({ column: key, sort, onSort: toggle }),
    order: (group) => (sort ? sortRows(group, columns[sort.key], sort.dir) : [...group]),
  };
}

export interface TableSort<T, K extends string> extends SortOrder<T, K> {
  /** The rows in the selected order (the given order until a header is selected). */
  rows: T[];
}

/**
 * Sorting of a table whose rows are all loaded. Selecting a header orders by that column (text
 * A–Z, numbers and dates largest or newest first); selecting it again reverses the order.
 * Tables that show one page of a larger set sort on the server instead.
 *
 * Declare `columns` outside the component (or memoize it) so the order is computed only when the
 * rows or the sort change.
 */
export function useTableSort<T, K extends string>(
  rows: readonly T[],
  columns: SortColumns<T, K>,
  initial: TableSortState<NoInfer<K>> | null = null,
): TableSort<T, K> {
  const state = useSortOrder(rows, columns, initial);
  const getter = state.sort ? columns[state.sort.key] : undefined;
  const dir = state.sort?.dir;
  const sorted = useMemo(
    () => (getter && dir ? sortRows(rows, getter, dir) : [...rows]),
    [rows, getter, dir],
  );
  return { ...state, rows: sorted };
}
